import React, { useState } from "react";
import { Icon } from "../icons";
import { fmtDate, fmtDateTime } from "../lib/format";
import {
  crewAssigned,
  sitCheckDate,
  sitCheckInfo,
  sitConfirmedDate,
  sitDatesState,
  sitOpsDate,
} from "../lib/sitLocal";
import { ReadField, SectionLabel } from "./fields";

/**
 * In Transit / Delivery Day pieces of the design of 2026-10-24 (Asana 1219846172081510):
 * Delivery type, ETA Communication, and the SIT Local section with the Operations
 * check-in (1219846172081517), the SIT Local log and the customer's feedback.
 */

const notesOf = (job) => (job.s2Notes && !Array.isArray(job.s2Notes) ? job.s2Notes : {});

const DELIVERY_TYPES = [
  ["", "Select delivery type"],
  ["driver", "Driver delivery"],
  ["sit-local", "SIT Local delivery"],
];

export const DeliveryTypeField = ({ job, actions }) => (
  <div className="field">
    <label htmlFor="delivery-type">Delivery type</label>
    <select
      id="delivery-type"
      value={notesOf(job).deliveryType || ""}
      onChange={(e) => actions.setS2Note(job.id, "deliveryType", e.target.value)}
    >
      {DELIVERY_TYPES.map(([value, label]) => (
        <option value={value} key={value || "none"}>
          {label}
        </option>
      ))}
    </select>
  </div>
);

const LogRows = ({ entries, empty, badge }) =>
  entries.length ? (
    entries.map((e, i) => (
      <div className="liab-row" style={{ alignItems: "flex-start" }} key={`${e.date}-${i}`}>
        <div className="liab-q">
          <div style={{ whiteSpace: "pre-wrap" }}>{e.text}</div>
          <div className="liab-sub">
            {badge ? badge(e).sub + " · " : ""}
            {e.author || "MC"} · {fmtDateTime(e.date)}
          </div>
        </div>
        {badge ? <span className={`badge ${badge(e).cls}`}>{badge(e).label}</span> : null}
      </div>
    ))
  ) : (
    <div className="liab-row">
      <div className="liab-sub">{empty}</div>
    </div>
  );

const ETA_FILTERS = [
  ["all", "All"],
  ["ld", "From Long Distance"],
  ["customer", "Update to the customer"],
];

const etaBadge = (e) =>
  e.kind === "customer"
    ? { cls: "resolved", label: "To customer", sub: "Update to the customer" }
    : { cls: "plain", label: "From LD", sub: "Update from Long Distance" };

/** ETA Communication: what Long Distance said and what the MC told the customer (the latter goes to Zoho). */
export const EtaCommunication = ({ job, actions }) => {
  const [filter, setFilter] = useState("all");
  const [kind, setKind] = useState("ld");
  const [text, setText] = useState("");
  const [error, setError] = useState(false);
  const log = Array.isArray(job.etaLog) ? job.etaLog : [];
  const shown = filter === "all" ? log : log.filter((e) => (e.kind === "customer" ? "customer" : "ld") === filter);
  const empty =
    filter === "customer"
      ? "No updates to the customer logged yet."
      : filter === "ld"
      ? "No updates from Long Distance logged yet."
      : "No ETA updates logged yet.";

  return (
    <>
      <div
        className="section-label"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}
      >
        <span>
          <Icon name="truck" />
          ETA Communication
        </span>
        <div className="seg" style={{ textTransform: "none", letterSpacing: 0 }}>
          {ETA_FILTERS.map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={filter === value ? "on-yes" : ""}
              aria-pressed={filter === value}
              onClick={() => {
                setFilter(value);
                if (value !== "all") setKind(value);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="liab-card">
        <LogRows entries={shown} empty={empty} badge={etaBadge} />
        <div className="liab-block">
          <div className="liab-row" style={{ padding: 0, border: "none", marginBottom: 8 }}>
            <div className="liab-q">New ETA update</div>
            <div className="seg">
              <button type="button" className={kind === "ld" ? "on-yes" : ""} onClick={() => setKind("ld")}>
                From Long Distance
              </button>
              <button
                type="button"
                className={kind === "customer" ? "on-yes" : ""}
                onClick={() => setKind("customer")}
              >
                Update to the customer
              </button>
            </div>
          </div>
          <textarea
            rows={2}
            aria-label="New ETA update"
            placeholder={
              kind === "customer"
                ? "What did you tell the customer about the ETA? (saved to Zoho as an ETA update)"
                : "Paste or type the ETA update you received from Long Distance..."
            }
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(false);
            }}
          />
          {error ? <div className="foot-error" style={{ marginTop: 6 }}>Type the update before saving.</div> : null}
          <div className="toolbar-row" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn primary small"
              onClick={() => {
                if (!text.trim()) {
                  setError(true);
                  return;
                }
                actions.addEtaLog(job.id, kind, text);
                setText("");
              }}
            >
              <Icon name="check" />
              Add to log
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const SIT_STATUS_LABEL = {
  "": "Not yet in SIT Local",
  "in-sit": "In SIT Local",
  confirmed: "SIT Local Confirmed",
};

/** Where the job is in SIT Local, derived from its queue: the MC changes it with the buttons, not here. */
/**
 * "SIT Local since" is set by the backend from the moment Zoho's stage became
 * SIT Local, so until then the field says it fills in on its own (Asana 1220092199815250).
 */
const SIT_LOCAL_STATUSES = ["ld-warehouse-received", "ld-sit-confirmed", "ld-delivery-local"];
export const SIT_SINCE_HINT = "Fills in when Zoho moves the job to SIT Local";

const sitStatusOf = (job) => {
  if (job.sitLocalConfirmed || job.status === "ld-sit-confirmed") return "confirmed";
  return job.status === "ld-warehouse-received" ? "in-sit" : "";
};

const checkBadgeClass = (cls) => (cls === "ok" ? "resolved" : cls === "bad" || cls === "warn" ? "open" : "plain");

const OpsCheckIn = ({ job, actions }) => {
  const n = notesOf(job);
  const info = sitCheckInfo(job) || { cls: "wait", label: "No check-in scheduled" };
  const [crewNote, setCrewNote] = useState(n.sitCrewNote || "");
  return (
    <>
      <SectionLabel icon="bell">Operations Check-in</SectionLabel>
      <div className="liab-card">
        <div className="liab-row">
          <div className="liab-q">
            <div>Status</div>
            <div className="liab-sub">Check with Operations 2 days before the delivery that a crew is assigned</div>
          </div>
          <span className={`badge ${checkBadgeClass(info.cls)}`}>{info.label}</span>
        </div>
        <div className="liab-row">
          <div className="liab-q">
            <div>Check-in date</div>
            <div className="liab-sub">
              {n.sitCheckDoneAt ? "Done " + fmtDateTime(n.sitCheckDoneAt) : "Adjust if the plan changes"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="date"
              className="date-inline"
              aria-label="Check-in date"
              value={sitCheckDate(job) || ""}
              onChange={(e) => actions.setSitCheckDate(job.id, e.target.value)}
            />
            <button
              type="button"
              className={`btn small ${n.sitCheckDoneAt ? "success" : "primary"}`}
              onClick={() => actions.confirmSitCheckin(job.id)}
            >
              <Icon name="check" />
              {n.sitCheckDoneAt ? "Check-in done" : "Confirm check-in"}
            </button>
          </div>
        </div>
        <div className="liab-row">
          <div className="liab-q">
            <div>Operations confirms a crew</div>
            <div className="liab-sub">
              {n.sitCrewOk === "yes" && n.sitCrewConfirmedAt
                ? "Confirmed " + fmtDateTime(n.sitCrewConfirmedAt)
                : job.localCrewAssignedAt
                ? "Dispatch assigned a crew in the planner " + fmtDateTime(job.localCrewAssignedAt)
                : "Has Operations assigned a crew for the delivery?"}
            </div>
          </div>
          <div className="seg">
            <button
              type="button"
              className={n.sitCrewOk === "yes" ? "on-yes" : ""}
              aria-pressed={n.sitCrewOk === "yes"}
              onClick={() => actions.setSitCrew(job.id, "yes")}
            >
              Yes
            </button>
            <button
              type="button"
              className={n.sitCrewOk === "no" ? "on-no" : ""}
              aria-pressed={n.sitCrewOk === "no"}
              onClick={() => actions.setSitCrew(job.id, "no")}
            >
              Not yet
            </button>
          </div>
        </div>
        <div className="liab-block">
          <input
            type="text"
            aria-label="Crew or notes from Operations"
            placeholder="Crew or notes from Operations (e.g. Local crew B, truck 4)"
            value={crewNote}
            onChange={(e) => setCrewNote(e.target.value)}
            onBlur={() => {
              if (crewNote !== (n.sitCrewNote || "")) actions.setS2Note(job.id, "sitCrewNote", crewNote);
            }}
          />
        </div>
      </div>
    </>
  );
};

const AddLogBox = ({ placeholder, buttonLabel = "Add to log", errorText, rows = 2, onAdd }) => {
  const [text, setText] = useState("");
  const [error, setError] = useState(false);
  return (
    <div className="liab-block">
      <textarea
        rows={rows}
        aria-label={placeholder}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setError(false);
        }}
      />
      {error && errorText ? <div className="foot-error" style={{ marginTop: 6 }}>{errorText}</div> : null}
      <div className="toolbar-row" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="btn primary small"
          onClick={() => {
            if (!text.trim()) {
              setError(true);
              return;
            }
            onAdd(text.trim());
            setText("");
          }}
        >
          <Icon name="check" />
          {buttonLabel}
        </button>
      </div>
    </div>
  );
};

/** The SIT Local section: status, warehouse dates, the two delivery dates, check-in, log, feedback. */
export const SitLocalSection = ({ job, actions }) => {
  const dates = sitDatesState(job);
  const inCheckIn = job.status === "ld-sit-confirmed" || job.status === "ld-delivery-local";
  const sitLog = Array.isArray(job.sitLog) ? job.sitLog : [];
  const feedbackLog = Array.isArray(job.sitFeedbackLog) ? job.sitFeedbackLog : [];

  return (
    <>
      <SectionLabel icon="truck">SIT Local</SectionLabel>
      {/* Per the prototype: SIT Local since first, then the status (Asana 1220099429071097, 5.3). */}
      <ReadField label="SIT Local since">
        {job.sitLocalSince ? fmtDateTime(job.sitLocalSince) : SIT_LOCAL_STATUSES.includes(job.status) ? "—" : (
          <span style={{ color: "var(--ink-500)", fontWeight: 500 }}>{SIT_SINCE_HINT}</span>
        )}
      </ReadField>
      <div className="field-row">
        <div className="field">
          <label htmlFor="sit-status">SIT Local status</label>
          <select id="sit-status" value={sitStatusOf(job)} disabled>
            {Object.entries(SIT_STATUS_LABEL).map(([v, l]) => (
              <option value={v} key={v || "none"}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Last changed</label>
          <div className="val">
            {job.sitLocalConfirmedAt
              ? `${fmtDateTime(job.sitLocalConfirmedAt)} · by ${job.sitLocalConfirmedBy || "MC"}`
              : "—"}
          </div>
          {/* The MC's SIT Local confirmation goes to Zoho as a note. */}
          <span className="badge plain" style={{ marginTop: 6 }}>
            {job.sitLocalConfirmedAt ? "Sent to Zoho" : "Not synced to Zoho yet"}
          </span>
        </div>
      </div>

      <div className="sit-panels">
        <div className="sit-panel">
          <div className="sit-panel-head">
            <Icon name="clipboard" />
            From Operations
          </div>
          <label htmlFor="sit-ops-date">Delivery date given by Operations</label>
          <input
            id="sit-ops-date"
            type="date"
            className="sit-date"
            value={sitOpsDate(job)}
            onChange={(e) => actions.setSitDate(job.id, "sitOpsDate", e.target.value)}
          />
        </div>
        <div className="sit-panel">
          <div className="sit-panel-head">
            <Icon name="phone" />
            Confirmed with the Customer
          </div>
          <label htmlFor="sit-confirmed-date">Delivery date confirmed by the move coordinator</label>
          <input
            id="sit-confirmed-date"
            type="date"
            className="sit-date"
            value={sitConfirmedDate(job)}
            onChange={(e) => actions.setSitDate(job.id, "sitConfirmedDate", e.target.value)}
          />
          <div className="liab-sub" style={{ marginTop: 12 }}>
            Confirm with the customer based on the date Operations gave
          </div>
        </div>
      </div>
      <div className={`sit-match ${dates.cls}`}>
        <Icon name={dates.cls === "ok" ? "check" : dates.cls === "bad" ? "alert" : "clock"} />
        {dates.text}
      </div>
      {job.status === "ld-sit-confirmed" && !crewAssigned(job) ? (
        <div className="sit-match bad">
          <Icon name="alert" />
          Confirm with Operations that a crew is assigned before moving to Delivery Day.
        </div>
      ) : null}

      {inCheckIn ? <OpsCheckIn job={job} actions={actions} key={job.id} /> : null}

      <SectionLabel icon="note">SIT Local Log</SectionLabel>
      <div className="liab-card">
        <LogRows entries={sitLog} empty="No messages yet. Paste the message from Operations below." />
        <AddLogBox
          placeholder={"Paste the message from Operations or add a comment, e.g. “Miles scheduled this move for delivery on the 12th.”"}
          onAdd={(t) => actions.addSitLog(job.id, t)}
        />
      </div>

      <SectionLabel icon="phone">Feedback from the Customer</SectionLabel>
      <div className="liab-card">
        {feedbackLog.length ? <LogRows entries={feedbackLog} empty="" /> : null}
        <AddLogBox
          rows={3}
          placeholder="What did the customer say when you called to confirm the date — accepted the date or asked for a different one?"
          buttonLabel="Save feedback"
          errorText="Type the customer's feedback before saving."
          onAdd={(t) => actions.addSitFeedback(job.id, t)}
        />
      </div>
    </>
  );
};

/** The delivery line under a SIT Local Confirmed card: date + check-in state. */
export const sitCardDelivery = (job) => {
  const delivery = sitConfirmedDate(job);
  return delivery ? { date: fmtDate(delivery), info: sitCheckInfo(job) } : null;
};
